import api from "./axios";

// ─── Result types ─────────────────────────────────────────────────────────────

export interface ImportResult {
  created: number;
  skipped: number;
  errors: string[];
}

export interface AcImportResult extends ImportResult {
  archived: number;
  deleted: number;
}

export interface AcBulkImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export type AcBulkImportMode = "upsert" | "create_only";

export interface CounselorImportResult extends ImportResult {
  temporaryCredentials: { email: string; tempPassword: string }[];
}

export type AcImportMode = "replace_archive" | "add" | "delete_replace";

// ─── Helper: trigger browser file download from a Blob ───────────────────────

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── API client ───────────────────────────────────────────────────────────────

export const importsApi = {
  // ── Imports ────────────────────────────────────────────────────────────────

  async importAcs(
    activityId: number,
    mode: AcImportMode,
    confirmed: boolean,
    file: File
  ): Promise<AcImportResult> {
    const form = new FormData();
    form.append("file", file);
    form.append("activityId", String(activityId));
    form.append("mode", mode);
    if (confirmed) form.append("confirmed", "true");
    const res = await api.post<AcImportResult>("/imports/acs", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async importAcsBulk(mode: AcBulkImportMode, file: File): Promise<AcBulkImportResult> {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", mode);
    const res = await api.post<AcBulkImportResult>("/imports/acs-bulk", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async importActivities(file: File): Promise<ImportResult> {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post<ImportResult>("/imports/activities", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async importCampers(file: File): Promise<ImportResult> {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post<ImportResult>("/imports/campers", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async importCounselors(file: File): Promise<CounselorImportResult> {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post<CounselorImportResult>("/imports/counselors", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  async importCabins(file: File): Promise<ImportResult> {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post<ImportResult>("/imports/cabins", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  // ── Templates & Exports (authenticated, triggered as browser download) ─────

  async downloadTemplate(type: string, activityId?: number): Promise<void> {
    const params: Record<string, string> = {};
    if (activityId !== undefined) params.activityId = String(activityId);
    const res = await api.get(`/imports/template/${type}`, {
      params,
      responseType: "blob",
    });
    triggerDownload(res.data as Blob, `${type}-template.csv`);
  },

  async downloadExport(type: string, activityId?: number, label?: string): Promise<void> {
    const params: Record<string, string> = {};
    if (activityId !== undefined) params.activityId = String(activityId);
    const res = await api.get(`/imports/export/${type}`, {
      params,
      responseType: "blob",
    });
    triggerDownload(res.data as Blob, `${label ?? type}-export.csv`);
  },
};
